import { useCallback, useEffect, useRef, useState } from "react";
import {
    DashboardOutlined,
    LineChartOutlined,
    PlayCircleOutlined,
    StopOutlined,
} from "@ant-design/icons";
import {
    Card,
    Flex,
    Spin,
    Table,
    Tabs,
    Typography,
    notification,
    theme,
    Tag,
} from "antd";

import {
    EVENT_BLE,
    maxDuracionPruebaSegundos,
} from "../../../../../Constantes";

import ReactECharts from "echarts-for-react";
import useBle from "../../../../../Hooks/Ble";
import useSimulacionBle from "../../../../../Hooks/useSimulacionBle";
import useRegistroRep from "../../../../../Hooks/useRegistroRep";
import {
    ACELERACION_GRAVEDAD_COP as ACELERACION_GRAVEDAD,
    DURACION_TARA_S,
    UMBRAL_MINIMO_FUERZA,
    calcularMetricasCOP,
    calcularTara,
    mediaPicoDesviacion,
    numeroFinito,
    procesarMuestraCOP,
} from "../cop";

const { useToken } = theme;

const Grafica = ({ opcionesGrafica }) => (
    <div style={{ height: 420 }}>
        <ReactECharts
            lazyUpdate
            theme="dark2"
            option={opcionesGrafica}
            style={{ width: "100%", height: "100%" }}
        />
    </div>
);

const Rep = ({ atleta, repKey }) => {
    const [, setSeed] = useState(null);
    const { token } = useToken();

    const datos_brutos = useRef([]);
    const [fases, setFases] = useState([]);
    const [parametros, setParametros] = useState([]);
    const [frames, setFrames] = useState([]);
    const [cargando_frames, setCargandoFrames] = useState(false);

    const [sistema_de_pesos_y_tiempos, setSistemaDePesoTiempo] = useState([]);
    const sistemaRef = useRef([]);

    const [copHistorial, setCopHistorial] = useState([]);
    const copHistorialRef = useRef([]);

    const [grabando, setGrabando] = useState(false);
    const ref_grabando = useRef(false);

    const [tarando, setTarando] = useState(false);
    const ref_tarando = useRef(false);
    const taraMuestrasRef = useRef([]);
    const taraTimerRef = useRef(null);

    const offsetsRef = useRef(Array(8).fill(0));
    const umbralesRef = useRef([
        UMBRAL_MINIMO_FUERZA,
        UMBRAL_MINIMO_FUERZA,
    ]);

    const maxDuracionSeg = maxDuracionPruebaSegundos(atleta);
    const maxDuracionRef = useRef(maxDuracionSeg);
    maxDuracionRef.current = maxDuracionSeg;

    const autoStopMaxDuracionRef = useRef(false);
    const detenerRef = useRef(() => Promise.resolve());

    const [url_video, setURLVideo] = useState(null);
    const [tabActivo, setTabActivo] = useState("1");

    const { resetearBalanza } = useBle();

    useSimulacionBle({
        // Activa también durante la tara: el buffer COP emite primero las
        // plataformas descargadas para calcular offsets/umbrales, igual que
        // el Python pide retirar toda carga antes de tarar.
        grabando,
        ref_grabando,
        tipo: "cop",
        duracionSimulacionSegundos: maxDuracionSeg,
        taraSegundos: DURACION_TARA_S,
    });

    const [primerTiempoMs, setPrimerTiempoMs] = useState(null);
    const primerTiempoRef = useRef(null);

    const getSnapshot = useCallback(
        () => ({
            sistema_de_pesos_y_tiempos,
            fases,
            parametros,
            datos: copHistorial,
            copHistorial,
            offsets: offsetsRef.current,
            umbrales: umbralesRef.current,
            datos_brutos: [...datos_brutos.current],
            frames,
        }),
        [
            sistema_de_pesos_y_tiempos,
            fases,
            parametros,
            copHistorial,
            frames,
        ]
    );

    const hasDatosRep = useCallback(
        () =>
            Array.isArray(sistema_de_pesos_y_tiempos) &&
            sistema_de_pesos_y_tiempos.length > 0,
        [sistema_de_pesos_y_tiempos]
    );

    useRegistroRep(repKey, getSnapshot, hasDatosRep);

    const opcionesFuerza = {
        tooltip: {
            trigger: "axis",
        },
        legend: {
            top: 5,
            data: ["P1", "P2", "Combinada"],
        },
        grid: {
            left: 65,
            right: 20,
            top: 45,
            bottom: 55,
        },
        xAxis: {
            type: "category",
            name: "Tiempo (s)",
            nameLocation: "middle",
            nameGap: 30,
            data: sistema_de_pesos_y_tiempos.map((s) =>
                Number(s.tiempo).toFixed(2)
            ),
        },
        yAxis: {
            type: "value",
            name: "Fuerza (N)",
            nameLocation: "middle",
            nameGap: 45,
        },
        series: [
            {
                name: "P1",
                type: "line",
                symbol: "none",
                data: sistema_de_pesos_y_tiempos.map(
                    (s) => s?.peso?.izq ?? s?.peso?.p1 ?? 0
                ),
                smooth: false,
            },
            {
                name: "P2",
                type: "line",
                symbol: "none",
                data: sistema_de_pesos_y_tiempos.map(
                    (s) => s?.peso?.der ?? s?.peso?.p2 ?? 0
                ),
                smooth: false,
            },
            {
                name: "Combinada",
                type: "line",
                symbol: "none",
                data: sistema_de_pesos_y_tiempos.map(
                    (s) => s?.peso?.combinada ?? 0
                ),
                smooth: false,
            },
        ],
    };

    const opcionesCOP = {
        tooltip: {
            trigger: "axis",
        },
        legend: {
            top: 5,
            data: ["COP P1", "COP P2", "COP combinado"],
        },
        grid: {
            left: 65,
            right: 20,
            top: 45,
            bottom: 55,
        },
        xAxis: {
            type: "value",
            name: "ML (mm)",
            nameLocation: "middle",
            nameGap: 30,
            min: "dataMin",
            max: "dataMax",
        },
        yAxis: {
            type: "value",
            name: "AP (mm)",
            nameLocation: "middle",
            nameGap: 50,
            min: "dataMin",
            max: "dataMax",
        },
        series: [
            {
                name: "COP P1",
                type: "line",
                symbol: "none",
                data: copHistorial
                    .filter((r) => r?.copP1Global?.every(Number.isFinite))
                    .map((r) => [
                        r.copP1Global[0],
                        r.copP1Global[1],
                    ]),
            },
            {
                name: "COP P2",
                type: "line",
                symbol: "none",
                data: copHistorial
                    .filter((r) => r?.copP2Global?.every(Number.isFinite))
                    .map((r) => [
                        r.copP2Global[0],
                        r.copP2Global[1],
                    ]),
            },
            {
                name: "COP combinado",
                type: "line",
                symbol: "none",
                data: copHistorial
                    .filter((r) => r?.copTotal?.every(Number.isFinite))
                    .map((r) => [
                        r.copTotal[0],
                        r.copTotal[1],
                    ]),
            },
        ],
    };

    const opcionesCOPTiemporal = {
        tooltip: {
            trigger: "axis",
        },
        legend: {
            top: 5,
            data: ["ML", "AP"],
        },
        grid: {
            left: 65,
            right: 20,
            top: 45,
            bottom: 55,
        },
        xAxis: {
            type: "category",
            name: "Tiempo (s)",
            nameLocation: "middle",
            nameGap: 30,
            data: copHistorial.map((r) =>
                Number(r.tiempo).toFixed(2)
            ),
        },
        yAxis: {
            type: "value",
            name: "Desplazamiento (mm)",
            nameLocation: "middle",
            nameGap: 50,
        },
        series: [
            {
                name: "ML",
                type: "line",
                symbol: "none",
                data: copHistorial.map((r) =>
                    Number.isFinite(r?.copTotal?.[0])
                        ? r.copTotal[0]
                        : null
                ),
            },
            {
                name: "AP",
                type: "line",
                symbol: "none",
                data: copHistorial.map((r) =>
                    Number.isFinite(r?.copTotal?.[1])
                        ? r.copTotal[1]
                        : null
                ),
            },
        ],
    };

    const finalizarTara = useCallback(async () => {
        try {
            setCargandoFrames(true);

            const muestras = taraMuestrasRef.current;

            // Compatibilidad con el simulador/flujo antiguo:
            // si no llegan 8 canales, se deja tara en cero y la prueba continúa,
            // pero el COP no podrá calcularse con 2 fuerzas agregadas.
            if (!muestras.length || muestras[0]?.length !== 8) {
                offsetsRef.current = Array(8).fill(0);
                umbralesRef.current = [
                    UMBRAL_MINIMO_FUERZA,
                    UMBRAL_MINIMO_FUERZA,
                ];

                notification.warning({
                    message: "Trama BLE sin 8 canales",
                    description:
                        "La prueba continuará, pero el COP requiere 8 sensores + tiempo por trama.",
                });
            } else {
                const resultadoTara = calcularTara(muestras);
                offsetsRef.current = resultadoTara.offsets;
                umbralesRef.current = resultadoTara.umbrales;

                notification.success({
                    message: "Tara completada",
                    description: `Umbrales P1=${resultadoTara.umbrales[0].toFixed(
                        2
                    )} N; P2=${resultadoTara.umbrales[1].toFixed(2)} N`,
                });
            }

            taraMuestrasRef.current = [];
            ref_tarando.current = false;
            setTarando(false);

            await resetearBalanza();

            setSistemaDePesoTiempo([]);
            sistemaRef.current = [];
            setCopHistorial([]);
            copHistorialRef.current = [];
            datos_brutos.current = [];
            primerTiempoRef.current = null;
            setPrimerTiempoMs(null);

            autoStopMaxDuracionRef.current = false;
            setTabActivo("1");

            // Sigue grabando; la simulación también continúa con este estado.
            setGrabando(true);
            ref_grabando.current = true;
        } catch (error) {
            ref_tarando.current = false;
            ref_grabando.current = false;
            setTarando(false);
            setGrabando(false);

            notification.error({
                message: "Error durante la tara",
                description: error?.message || String(error),
            });
        } finally {
            setCargandoFrames(false);
        }
    }, [resetearBalanza]);

    const iniciar = async () => {
        try {
            autoStopMaxDuracionRef.current = false;

            setFases([]);
            setFrames([]);
            setParametros([]);
            setSistemaDePesoTiempo([]);
            sistemaRef.current = [];
            setCopHistorial([]);
            copHistorialRef.current = [];

            datos_brutos.current = [];

            offsetsRef.current = Array(8).fill(0);
            umbralesRef.current = [
                UMBRAL_MINIMO_FUERZA,
                UMBRAL_MINIMO_FUERZA,
            ];

            primerTiempoRef.current = null;
            setPrimerTiempoMs(null);
            setTabActivo("1");

            await resetearBalanza();

            // Durante la tara también escuchamos BLE. Esto permite que el
            // simulador existente pueda alimentar las muestras de tara.
            taraMuestrasRef.current = [];
            ref_tarando.current = true;
            ref_grabando.current = true;

            setTarando(true);
            setGrabando(true);
            setCargandoFrames(true);

            clearTimeout(taraTimerRef.current);
            taraTimerRef.current = setTimeout(() => {
                void finalizarTara();
            }, DURACION_TARA_S * 1000);
        } catch (error) {
            ref_tarando.current = false;
            ref_grabando.current = false;
            setTarando(false);
            setGrabando(false);

            notification.error({
                message: "No se pudo iniciar la prueba",
                description: error?.message || String(error),
            });
        }
    };

    function onBle({ detail }) {
        if (!Array.isArray(detail) || !detail.length) return;
        if (!ref_grabando.current) return;

        const valores = detail.map(numeroFinito);
        const tieneCOP = valores.length >= 9;

        // -------------------- TARA --------------------
        if (ref_tarando.current) {
            if (tieneCOP) {
                taraMuestrasRef.current.push(valores.slice(0, 8));
            }
            return;
        }

        // -------------------- TRAMA COP --------------------
        if (tieneCOP) {
            const muestraCruda = valores.slice(0, 8);
            const tiempoTramaMs = valores[8];

            if (
                !muestraCruda.every(Number.isFinite) ||
                !Number.isFinite(tiempoTramaMs)
            ) {
                return;
            }

            if (primerTiempoRef.current === null) {
                primerTiempoRef.current = tiempoTramaMs;
                setPrimerTiempoMs(tiempoTramaMs);
            }

            const tiempo = Math.max(
                0,
                (tiempoTramaMs - primerTiempoRef.current) / 1000
            );

            let resultado;

            try {
                resultado = procesarMuestraCOP(
                    muestraCruda,
                    offsetsRef.current,
                    umbralesRef.current
                );
            } catch (error) {
                notification.error({
                    message: "Trama COP inválida",
                    description: error.message,
                });
                return;
            }

            datos_brutos.current.push(
                [
                    datos_brutos.current.length + 1,
                    tiempoTramaMs,
                    tiempo,
                    ...muestraCruda,
                ].join(",")
            );

            const siguiente = {
                anterior: tiempoTramaMs,
                tiempo,
                masa: {
                    p1: resultado.fzP1 / ACELERACION_GRAVEDAD,
                    p2: resultado.fzP2 / ACELERACION_GRAVEDAD,
                    izq: resultado.fzP1 / ACELERACION_GRAVEDAD,
                    der: resultado.fzP2 / ACELERACION_GRAVEDAD,
                    combinada: resultado.fzTotal / ACELERACION_GRAVEDAD,
                },
                peso: {
                    p1: resultado.fzP1,
                    p2: resultado.fzP2,
                    izq: resultado.fzP1,
                    der: resultado.fzP2,
                    combinada: resultado.fzTotal,
                },
                cop: {
                    p1Local: resultado.copP1Local,
                    p2Local: resultado.copP2Local,
                    p1Global: resultado.copP1Global,
                    p2Global: resultado.copP2Global,
                    total: resultado.copTotal,
                },
            };

            sistemaRef.current = [...sistemaRef.current, siguiente];

            setSistemaDePesoTiempo((prev) => [
                ...prev,
                siguiente,
            ]);

            const registroCOP = {
                tiempoMs: tiempoTramaMs,
                tiempo,
                fzP1: resultado.fzP1,
                fzP2: resultado.fzP2,
                fzTotal: resultado.fzTotal,
                copP1Local: resultado.copP1Local,
                copP2Local: resultado.copP2Local,
                copP1Global: resultado.copP1Global,
                copP2Global: resultado.copP2Global,
                copTotal: resultado.copTotal,
            };

            copHistorialRef.current = [
                ...copHistorialRef.current,
                registroCOP,
            ];
            setCopHistorial(copHistorialRef.current);

            if (
                tiempo >= maxDuracionRef.current &&
                !autoStopMaxDuracionRef.current
            ) {
                autoStopMaxDuracionRef.current = true;
                setTimeout(() => {
                    void detenerRef.current?.();
                }, 0);
            }

            return;
        }

        // -------------------- COMPATIBILIDAD CON TU TRAMA ANTERIOR --------------------
        // Tu implementación anterior recibía: fuerzaP1, fuerzaP2, tiempo.
        if (valores.length >= 3) {
            const p1 = valores[0];
            const p2 = valores[1];
            const tiempoMs = valores[2];

            if (
                !Number.isFinite(p1) ||
                !Number.isFinite(p2) ||
                !Number.isFinite(tiempoMs)
            ) {
                return;
            }

            if (primerTiempoRef.current === null) {
                primerTiempoRef.current = tiempoMs;
                setPrimerTiempoMs(tiempoMs);
            }

            const tiempo =
                Math.max(
                    0,
                    (tiempoMs - primerTiempoRef.current) / 1000
                );

            const siguiente = {
                anterior: tiempoMs,
                tiempo,
                masa: {
                    combinada: p1 + p2,
                    izq: p1,
                    der: p2,
                },
                peso: {
                    combinada: (p1 + p2) * ACELERACION_GRAVEDAD,
                    izq: p1 * ACELERACION_GRAVEDAD,
                    der: p2 * ACELERACION_GRAVEDAD,
                },
            };

            datos_brutos.current.push(
                [datos_brutos.current.length + 1, tiempoMs, tiempo, p1, p2].join(
                    ","
                )
            );

            sistemaRef.current = [...sistemaRef.current, siguiente];
            setSistemaDePesoTiempo((prev) => [...prev, siguiente]);

            if (
                tiempo >= maxDuracionRef.current &&
                !autoStopMaxDuracionRef.current
            ) {
                autoStopMaxDuracionRef.current = true;
                setTimeout(() => {
                    void detenerRef.current?.();
                }, 0);
            }
        }
    }

    async function detener() {
        setGrabando(false);
        ref_grabando.current = false;

        if (tarando) {
            clearTimeout(taraTimerRef.current);
            ref_tarando.current = false;
            setTarando(false);
        }

        await esperar(350);

        try {
            await calcularParametros();
        } catch (error) {
            notification.error({
                description:
                    error?.message ||
                    "Descarte la prueba: no se obtuvieron datos válidos.",
            });
        }
    }

    const esperar = (t) =>
        new Promise((resolve) => setTimeout(resolve, t));

    const calcularParametros = async () => {
        try {
            setCargandoFrames(true);

            const datos =
                sistemaRef.current.length
                    ? sistemaRef.current
                    : sistema_de_pesos_y_tiempos;

            if (!datos.length) {
                throw new Error("Sin datos para calcular.");
            }

            if (copHistorialRef.current.length) {
                const resultado = calcularMetricasCOP(
                    copHistorialRef.current
                );

                setParametros(resultado);
            } else {
                // Trama antigua: conserva los parámetros de fuerza que ya
                // podía generar tu componente, pero avisa que no hay COP.
                const fzP1 = datos.map((d) => d?.peso?.izq ?? 0);
                const fzP2 = datos.map((d) => d?.peso?.der ?? 0);
                const fzTotal = datos.map(
                    (d) => d?.peso?.combinada ?? 0
                );

                const total = mediaPicoDesviacion(fzTotal);
                const izq = mediaPicoDesviacion(fzP1);
                const der = mediaPicoDesviacion(fzP2);

                setParametros([
                    {
                        key: "fuerza-promedio",
                        nombre: "Fuerza promedio",
                        valor: total.media,
                        u: "N",
                        categoria: "Fuerza",
                    },
                    {
                        key: "fuerza-pico",
                        nombre: "Fuerza pico",
                        valor: total.pico,
                        u: "N",
                        categoria: "Fuerza",
                    },
                    {
                        key: "fuerza-promedio-izq",
                        nombre: "Fuerza promedio izquierda",
                        valor: izq.media,
                        u: "N",
                        categoria: "Fuerza",
                    },
                    {
                        key: "fuerza-promedio-der",
                        nombre: "Fuerza promedio derecha",
                        valor: der.media,
                        u: "N",
                        categoria: "Fuerza",
                    },
                ]);

                notification.warning({
                    message: "COP no calculado",
                    description:
                        "El dispositivo actual está enviando 2 fuerzas agregadas. Para COP necesita la trama de 8 sensores + TIEMPO_MS.",
                });
            }

            setFases([]);
            notification.success({
                message: "Parámetros calculados",
                description: copHistorialRef.current.length
                    ? "COP, fuerzas y sway calculados."
                    : "Fuerzas calculadas.",
            });
        } catch (err) {
            console.log(err);
            notification.error({
                description: err?.message || String(err),
            });
            throw err;
        } finally {
            setCargandoFrames(false);
        }
    };

    detenerRef.current = detener;

    useEffect(() => {
        setSeed(Date.now());

        document.addEventListener(EVENT_BLE, onBle);

        return () => {
            clearTimeout(taraTimerRef.current);
            document.removeEventListener(EVENT_BLE, onBle);
        };
        // El handler utiliza refs para datos mutables y debe registrarse una
        // sola vez durante la vida del componente.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const ultimoCOP = copHistorial.length
        ? copHistorial[copHistorial.length - 1]
        : null;

    const tabs = [
        {
            label: "Gráficas",
            key: "1",
            icon: <LineChartOutlined />,
            children: (
                <Flex vertical gap="middle" style={{ padding: 8 }}>
                    <Card title="Fuerza vertical">
                        <Grafica opcionesGrafica={opcionesFuerza} />
                    </Card>

                    <Card title="Trayectoria COP global">
                        <Grafica opcionesGrafica={opcionesCOP} />
                    </Card>

                    <Card title="COP combinado en el tiempo">
                        <Grafica opcionesGrafica={opcionesCOPTiemporal} />
                    </Card>
                </Flex>
            ),
        },
        {
            label: "Parámetros",
            key: "2",
            icon: <DashboardOutlined />,
            children: (
                <div
                    style={{
                        padding: 16,
                        width: "100%",
                        maxWidth: "100%",
                        minWidth: 0,
                        boxSizing: "border-box",
                    }}
                >
                    <Flex gap="middle" wrap="wrap">
                        <Card size="small" title="P1" style={{ minWidth: 180 }}>
                            <Typography.Text>
                                Fz:{" "}
                                {ultimoCOP
                                    ? ultimoCOP.fzP1.toFixed(2)
                                    : "—"}{" "}
                                N
                            </Typography.Text>
                            <br />
                            <Typography.Text>
                                COP:{" "}
                                {ultimoCOP &&
                                ultimoCOP.copP1Local?.every(
                                    Number.isFinite
                                )
                                    ? `(${ultimoCOP.copP1Local[0].toFixed(
                                          1
                                      )}, ${ultimoCOP.copP1Local[1].toFixed(
                                          1
                                      )}) mm`
                                    : "sin contacto"}
                            </Typography.Text>
                        </Card>

                        <Card size="small" title="P2" style={{ minWidth: 180 }}>
                            <Typography.Text>
                                Fz:{" "}
                                {ultimoCOP
                                    ? ultimoCOP.fzP2.toFixed(2)
                                    : "—"}{" "}
                                N
                            </Typography.Text>
                            <br />
                            <Typography.Text>
                                COP:{" "}
                                {ultimoCOP &&
                                ultimoCOP.copP2Local?.every(
                                    Number.isFinite
                                )
                                    ? `(${ultimoCOP.copP2Local[0].toFixed(
                                          1
                                      )}, ${ultimoCOP.copP2Local[1].toFixed(
                                          1
                                      )}) mm`
                                    : "sin contacto"}
                            </Typography.Text>
                        </Card>

                        <Card
                            size="small"
                            title="COP combinado"
                            style={{ minWidth: 220 }}
                        >
                            <Typography.Text>
                                {ultimoCOP &&
                                ultimoCOP.copTotal?.every(
                                    Number.isFinite
                                )
                                    ? `(${ultimoCOP.copTotal[0].toFixed(
                                          1
                                      )}, ${ultimoCOP.copTotal[1].toFixed(
                                          1
                                      )}) mm`
                                    : "—"}
                            </Typography.Text>
                        </Card>
                    </Flex>

                    <br />

                    <Flex gap="small" wrap="wrap">
                        <Tag>
                            Tara: {tarando ? "en curso" : "completada"}
                        </Tag>
                        <Tag>
                            Muestras: {copHistorial.length}
                        </Tag>
                        <Tag>
                            P1 umbral: {umbralesRef.current[0].toFixed(2)} N
                        </Tag>
                        <Tag>
                            P2 umbral: {umbralesRef.current[1].toFixed(2)} N
                        </Tag>
                    </Flex>

                    <br />

                    <Table
                        dataSource={parametros}
                        pagination={false}
                        size="small"
                        tableLayout="fixed"
                        rowKey={(row, index) =>
                            row?.key ?? `${row?.nombre ?? "p"}-${index}`
                        }
                        scroll={{ x: 700, y: 480 }}
                        style={{ width: "100%" }}
                    >
                        <Table.Column
                            title="Categoría"
                            dataIndex="categoria"
                            width={120}
                        />
                        <Table.Column
                            title="Parámetro"
                            dataIndex="nombre"
                            ellipsis
                            width={360}
                        />
                        <Table.Column
                            title="Valor"
                            dataIndex="valor"
                            width={180}
                            render={(v, record) => {
                                const n = Number(v);
                                const cuerpo = Number.isFinite(n)
                                    ? n.toFixed(2)
                                    : "—";

                                return (
                                    <Typography.Text>
                                        {cuerpo}
                                        {record?.u
                                            ? ` ${record.u}`
                                            : ""}
                                    </Typography.Text>
                                );
                            }}
                        />
                    </Table>
                </div>
            ),
        },
    ];

    return (
        <Flex
            justify="space-between"
            style={{
                flex: 1,
                minWidth: 0,
                width: "100%",
            }}
        >
            <div
                style={{
                    flex: 1,
                    minWidth: 0,
                    maxWidth: "100%",
                    overflow: "hidden",
                }}
            >
                <Tabs
                    style={{ flex: 1 }}
                    tabPosition="left"
                    items={tabs}
                    activeKey={tabActivo}
                    onChange={setTabActivo}
                />
            </div>

            <Flex
                style={{ width: "30%", minWidth: 190 }}
                vertical
                justify="flex-start"
                align="center"
            >
                <br />

                {tarando ? (
                    <Flex vertical gap="middle" align="center">
                        <Spin size="large" />
                        <Typography.Text style={{ textAlign: "center" }}>
                            Tarando plataformas...
                        </Typography.Text>
                        <Typography.Text type="secondary">
                            Retira toda carga durante {DURACION_TARA_S} s
                        </Typography.Text>
                    </Flex>
                ) : null}

                {!tarando &&
                !grabando &&
                !sistema_de_pesos_y_tiempos.length ? (
                    <Flex vertical gap="middle" align="center">
                        <PlayCircleOutlined
                            style={{
                                fontSize: 72,
                                color: token.colorPrimary,
                            }}
                            onClick={iniciar}
                        />
                        <Typography.Text style={{ textAlign: "center" }}>
                            Iniciar prueba COP
                        </Typography.Text>
                    </Flex>
                ) : null}

                {!tarando && grabando ? (
                    <Flex vertical gap="middle" align="center">
                        <StopOutlined
                            style={{
                                fontSize: 72,
                                color: token.colorPrimary,
                            }}
                            onClick={detener}
                        />
                        <Typography.Text style={{ textAlign: "center" }}>
                            Detener prueba
                        </Typography.Text>
                    </Flex>
                ) : null}

                {cargando_frames && !tarando ? (
                    <Spin spinning>
                        <Typography.Text>
                            Calculando parámetros
                        </Typography.Text>
                    </Spin>
                ) : null}

                {primerTiempoMs !== null ? (
                    <Typography.Text type="secondary" style={{ marginTop: 12 }}>
                        Inicio trama: {primerTiempoMs.toFixed(0)} ms
                    </Typography.Text>
                ) : null}
            </Flex>
        </Flex>
    );
};

export default Rep;
